import { InteractionType } from '@azure/msal-browser'
import { MsalAuthenticationTemplate } from '@azure/msal-react'
import { MsalErrorComponent } from '@components/MsalErrorComponent.tsx'
import { Toaster } from '@components/ui/sonner'
import { RoleBasedLayout } from '@layouts/RoleBasedLayout'
import { createRootRouteWithContext, Outlet, redirect, useLocation } from '@tanstack/react-router'
import { loginRequestApi } from '@utils/authConfig.ts'
import type { msalInstance } from '@utils/msal.ts'
import { getUserPermissions } from '@utils/userPermissions'
import { NuqsAdapter } from 'nuqs/adapters/tanstack-router'
import { TenantProvider } from '@/contexts/TenantContext'
import type { queryClient } from '@/main.tsx'

const RootComponent = () => {
  const location = useLocation()
  const isUnauthorized = location.pathname === '/unauthorized'

  return (
    <NuqsAdapter>
      <Toaster />
      <MsalAuthenticationTemplate
        interactionType={InteractionType.Redirect}
        authenticationRequest={loginRequestApi}
        errorComponent={MsalErrorComponent}
      >
        {isUnauthorized ? (
          <Outlet />
        ) : (
          <TenantProvider>
            <RoleBasedLayout>
              <Outlet />
            </RoleBasedLayout>
          </TenantProvider>
        )}
      </MsalAuthenticationTemplate>
    </NuqsAdapter>
  )
}

export const Route = createRootRouteWithContext<{
  queryClient: typeof queryClient
  auth: typeof msalInstance
}>()({
  beforeLoad: async ({ location }) => {
    const isUnauthorizedPage = location.pathname === '/unauthorized'

    const permissions = await getUserPermissions({
      forceRefresh: isUnauthorizedPage,
    })

    if (!permissions.hasSkogHubUserAccess) {
      if (!isUnauthorizedPage) {
        // eslint-disable-next-line @typescript-eslint/only-throw-error
        throw redirect({
          to: '/unauthorized',
        })
      }
      return
    }

    if (isUnauthorizedPage) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({
        to: '/',
      })
    }
  },
  component: RootComponent,
})
